<template>
  <template v-if="project.permissions.edit">
    <button
      class="c-button c-button--small c-button--dark u-mbs"
      @click="releaseCreateOpen = true"
    >
      <SVGIcon class="c-button__icon" name="add" />
      <span class="c-button__label">{{ t('createNewRelease') }}</span>
    </button>
    <Modal v-model:open="releaseCreateOpen" :title="t('createARelease')">
      <ProjectReleaseForm
        v-model="newRelease"
        :error="createRelease.err"
        :loading="createRelease.loading"
        @submit="createRelease.exec"
      />
    </Modal>
  </template>
  <template v-if="releases.length > 0">
    <ProjectRelease
      v-for="release of releases"
      :key="release"
      :release-id="release"
      :project="project"
      @deleted="onDeleteRelease(release)"
    />
  </template>
  <template v-else>
    <p class="u-color-disabled">
      <small>{{ project.permissions.edit ?
        t('createFirstRelease') :
        t('noRelease')
      }}</small>
    </p>
  </template>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useRequest, getProject, createProjectRelease } from '@/utils/api'
import { useI18n } from '@/utils/i18n'

import SVGIcon from '@/components/SVGIcon.vue'
import Modal from '@/components/Modal.vue'
import ProjectRelease from '@/components/ProjectRelease.vue'
import ProjectReleaseForm, { ReleaseForm } from '@/components/ProjectReleaseForm.vue'

const props = defineProps<{
  projectId: number
}>()

const { t } = useI18n({
  messages: {
    en: {
      createFirstRelease: 'Create your first release by clicking the above button.',
      noRelease: "There aren't any releases yet.",
      createNewRelease: 'Create a new release',
      createARelease: 'Create a release'
    },
    fr: {
      createFirstRelease: ' Créez votre première release en cliquant sur le bouton ci-dessus. ',
      noRelease: 'Aucune release pour le moment.',
      createNewRelease: 'Créer une nouvelle release',
      createARelease: 'Créer une release'
    }
  }
})

const project = await getProject(props.projectId)
const releases = ref<number[]>(project.releases.map(r => r.pk))

const initialNewRelease = () => ({
  name: '',
  description: '',
  releaseNotes: '',
  assets: []
})

const newRelease = ref<ReleaseForm>(initialNewRelease())

const createRelease = useRequest(async () => {
  const created = await createProjectRelease({
    name: newRelease.value.name,
    description: newRelease.value.description,
    release_notes: newRelease.value.releaseNotes,
    assets: newRelease.value.assets.map(a => a.pk),
    project_id: project.pk.toString()
  })
  // reset form
  newRelease.value = initialNewRelease()
  // close modal
  releaseCreateOpen.value = false
  // remove from release list
  releases.value.unshift(created.release_pk)
})

function onDeleteRelease (releaseId: number) {
  releases.value = releases.value.filter(r => r !== releaseId)
}

const releaseCreateOpen = ref(false)
</script>
